import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import Pagination from "../../../components/UI/Pagination";
import {
  getPatientMedications,
  createPatientMedication,
  updatePatientMedication,
  deletePatientMedication,
  getPatientMedicationPrintData,
} from "../../../services/patientService";
import { searchRxnormMedications } from "../../../services/medicationService";
import {
  PillIcon,
  PlusIcon,
  PrintIcon,
  EditIcon,
  DeleteIcon,
  CheckIcon,
  CloseIcon,
} from "../../../Icons";
import {
  displayDateUS,
  formatDateForInput,
  toIsoDate,
  displayDate,
} from "../../../utils/dateUtils";
import {
  MEDICATION_ROUTES,
  MEDICATION_FREQUENCIES,
} from "./medication.constants";
import YearJumpPopover from "../../../components/datePicker/YearJumpPopover";
import { nativeDatePickerSupportsYearJump } from "../../../components/datePicker/nativeYearJump";
import CollapsibleSection, {
  useCollapseOpenForCount,
} from "./CollapsibleSection";
import AddMedicationModal from "./AddMedicationModal";
import { SearchMenuPortal } from "./SearchMenuPortal";
import { SearchFieldStatusIcon } from "./MultiRowFields";
import { buildPhysicians } from "../patientForm.shared";
import useConfirm from "../../../hooks/useConfirm";
import { PATIENT_SECTION_TONE, PATIENT_THEME, PATIENT_THEME_HEX } from "../patientTheme";

const supportsNativeYearJump = nativeDatePickerSupportsYearJump();

const MEDICATION_TONE = PATIENT_SECTION_TONE;

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

const SearchMenuLoading = () => (
  <div className="px-3 py-5 flex items-center justify-center gap-2.5 text-sm text-slate-500">
    <span className="w-4 h-4 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin shrink-0" />
    <span>Searching…</span>
  </div>
);

/** Prefer RxNorm ID when already selected from catalog. */
function resolveMedicationSearchQuery(value, medicationId) {
  const id = String(medicationId || "").trim();
  if (id) return id;
  return String(value || "").trim();
}

const fieldClass = `w-full h-9 px-2.5 text-sm border border-slate-200 rounded-md bg-white outline-none transition-shadow ${PATIENT_THEME.fieldFocus}`;
const fieldClassLg = `w-full h-9 px-3 text-sm border border-slate-200 rounded-md bg-white outline-none transition-shadow ${PATIENT_THEME.fieldFocus}`;
const labelClass = "block text-[11px] font-semibold text-slate-600 mb-1";
const outlineActionBtn =
  "inline-flex items-center justify-center gap-1.5 h-7 px-2.5 text-xs font-medium rounded-md border bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const outlineNeutralBtn = `${outlineActionBtn} text-slate-700 border-slate-300 hover:bg-slate-50`;
const cellFieldClass = `w-full h-8 px-2 text-xs border border-indigo-200 rounded-md bg-white outline-none ${PATIENT_THEME.focus}`;
const addBtnClass = `inline-flex items-center justify-center gap-1.5 h-9 px-4 text-sm font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${PATIENT_THEME.button.soft}`;

const formatPhysicianLabel = (p) =>
  p?.npiNumber ? `${p.physicianName} (NPI: ${p.npiNumber})` : p?.physicianName || "";

const ChevronDownIcon = ({ size = 14 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="m6 8 4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Dropdown only when patient has physician records; otherwise free-text input.
 * Chevron is an explicit overlay so it never disappears behind long labels.
 */
const PhysicianField = ({
  options = [],
  value,
  onChange,
  inputClassName = fieldClass,
  placeholder = "Type physician name",
}) => {
  const hasPhysicians = options.length > 0;
  if (!hasPhysicians) {
    return (
      <input
        type="text"
        className={inputClassName}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    );
  }

  const selected = options.find((p) => p.physicianName === value);
  const selectValue = selected?.physicianName ?? "";

  return (
    <div className="relative min-w-0 w-full">
      <select
        className={`${inputClassName} appearance-none pr-9 min-w-0 max-w-full truncate`}
        value={selectValue}
        title={formatPhysicianLabel(selected) || "Select physician"}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Select physician</option>
        {options.map((p) => (
          <option
            key={p.id ?? p.physicianName}
            value={p.physicianName}
            title={formatPhysicianLabel(p)}
          >
            {formatPhysicianLabel(p)}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500">
        <ChevronDownIcon size={14} />
      </span>
    </div>
  );
};

/** Controlled date field — always shows / stores mm/dd/yyyy in the UI. */
const UsDateField = ({ label, value, onChange, inputClassName = fieldClass }) => {
  const dateRef = useRef(null);
  const anchorRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const ymd = formatDateForInput(value) || "";
  const display = value
    ? displayDateUS(value) ||
      (String(value).match(/^\d{2}\/\d{2}\/\d{4}$/) ? value : "")
    : "";

  const [localText, setLocalText] = useState(display);

  useEffect(() => {
    setLocalText(display);
  }, [display]);

  const emitFromText = (text) => {
    const m = String(text).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m) {
      onChange(`${m[1]}/${m[2]}/${m[3]}`);
      return;
    }
    if (!text) onChange("");
  };

  const openPicker = () => {
    if (!supportsNativeYearJump) {
      setPickerOpen(true);
      return;
    }
    if (dateRef.current?.showPicker) {
      try {
        dateRef.current.showPicker();
      } catch {
        dateRef.current.focus();
      }
    } else {
      dateRef.current?.focus();
    }
  };

  return (
    <div className="min-w-[9.75rem] w-full">
      {label ? <label className={labelClass}>{label}</label> : null}
      <div className="relative" ref={anchorRef}>
        <input
          type="text"
          className={`${inputClassName} pr-9 tracking-wide`}
          value={localText}
          placeholder="mm/dd/yyyy"
          maxLength={10}
          size={10}
          style={{ minWidth: "9.75rem" }}
          onChange={(e) => {
            let val = e.target.value.replace(/[^0-9/]/g, "");
            if (val.length === 2 && localText.length === 1 && !val.includes("/")) {
              val += "/";
            } else if (
              val.length === 5 &&
              localText.length === 4 &&
              val.split("/").length === 2
            ) {
              val += "/";
            }
            setLocalText(val);
            emitFromText(val);
          }}
          onBlur={() => {
            if (localText) setLocalText(localText.replace(/-/g, "/"));
          }}
        />
        <input
          type="date"
          ref={dateRef}
          value={ymd}
          tabIndex={-1}
          className="absolute w-0 h-0 opacity-0 pointer-events-none"
          onChange={(e) => {
            const next = e.target.value;
            if (!next) {
              setLocalText("");
              onChange("");
              return;
            }
            const shown = displayDateUS(next) || displayDate(next)?.replace(/-/g, "/") || "";
            setLocalText(shown);
            onChange(shown);
          }}
        />
        <button
          type="button"
          onClick={openPicker}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          title="Open calendar"
          aria-label={`Pick ${label || "date"}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
          </svg>
        </button>
        {!supportsNativeYearJump && (
          <YearJumpPopover
            open={pickerOpen}
            anchorRef={anchorRef}
            value={ymd}
            onClose={() => setPickerOpen(false)}
            onSelect={(next) => {
              const shown = displayDateUS(next) || displayDate(next)?.replace(/-/g, "/") || "";
              setLocalText(shown);
              onChange(shown);
              setPickerOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

const mapCatalogRoute = (route) => {
  if (!route) return MEDICATION_ROUTES[0];
  if (MEDICATION_ROUTES.includes(route)) return route;
  return "Other";
};

const applyCatalogSelection = (prev, item) => ({
  ...prev,
  medicationId: item.medicationId || "",
  medicationName: item.medicationName || "",
  dosage: item.dosage || prev.dosage || "",
  route: mapCatalogRoute(item.route),
  catalogMeta: {
    brandName: item.brandName || "",
    genericName: item.genericName || "",
    doseForm: item.doseForm || "",
    tty: item.tty || "",
  },
});

const emptyForm = () => ({
  medicationId: "",
  medicationName: "",
  dosage: "",
  frequencySelect: MEDICATION_FREQUENCIES[0],
  frequencyOther: "",
  route: MEDICATION_ROUTES[0],
  prescribingPhysician: "",
  startDate: "",
  endDate: "",
  status: "Active",
  notes: "",
  catalogMeta: null,
});

const resolveFrequency = (form) => {
  if (form.frequencySelect === "Other") {
    return form.frequencyOther.trim();
  }
  return form.frequencySelect;
};

const medicationToForm = (med) => {
  const freq = med.frequency || "";
  const isPreset = MEDICATION_FREQUENCIES.includes(freq) && freq !== "Other";
  return {
    medicationId: med.medicationId || "",
    medicationName: med.medicationName || "",
    dosage: med.dosage || "",
    frequencySelect: isPreset ? freq : freq ? "Other" : MEDICATION_FREQUENCIES[0],
    frequencyOther: isPreset ? "" : freq,
    route: med.route || MEDICATION_ROUTES[0],
    prescribingPhysician: med.prescribingPhysician || "",
    startDate: displayDateUS(med.startDate) || "",
    endDate: displayDateUS(med.endDate) || "",
    status: med.status || "Active",
    notes: med.notes || "",
    catalogMeta: null,
  };
};

const toApiDate = (value) => {
  if (!value) return null;
  const mdy = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (mdy) {
    return toIsoDate(`${mdy[3]}-${mdy[1]}-${mdy[2]}`);
  }
  return toIsoDate(value);
};

const formToPayload = (form) => ({
  medicationId: form.medicationId.trim() || null,
  medicationName: form.medicationName.trim(),
  dosage: form.dosage.trim(),
  frequency: resolveFrequency(form),
  route: form.route,
  prescribingPhysician: form.prescribingPhysician.trim() || null,
  startDate: toApiDate(form.startDate),
  endDate: toApiDate(form.endDate),
  status: form.status || "Active",
  // Always a string — empty when unused (backend also accepts null).
  notes: form.notes?.trim() || "",
});

const validateForm = (form) => {
  if (!form.medicationName.trim()) return "Medication name is required";
  if (!form.dosage.trim()) return "Dosage is required";
  const frequency = resolveFrequency(form);
  if (!frequency) return "Frequency is required";
  if (!form.route) return "Route is required";
  return null;
};

const patientDisplayName = (patient) => {
  if (!patient) return "Patient";
  const name = [patient.lastName, patient.firstName].filter(Boolean).join(", ");
  return name || "Patient";
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const dash = (value) => {
  const s = value == null ? "" : String(value).trim();
  return s || "—";
};

const buildPrintHtml = (printData) => {
  const patient = printData.patient || {};
  const meds = printData.medications || [];
  const generatedAt = displayDateUS(printData.generatedAt) || displayDateUS(new Date());
  const patientName = patientDisplayName(patient);
  const mrn = patient.mrn || "—";
  const brand = PATIENT_THEME_HEX;

  const rows = meds
    .map(
      (m) => `
      <tr>
        <td class="mono">${escapeHtml(dash(m.medicationId))}</td>
        <td class="name">${escapeHtml(dash(m.medicationName))}</td>
        <td>${escapeHtml(dash(m.dosage))}</td>
        <td>${escapeHtml(dash(m.frequency))}</td>
        <td>${escapeHtml(dash(m.route))}</td>
        <td>${escapeHtml(dash(m.prescribingPhysician))}</td>
        <td class="date">${displayDateUS(m.startDate) || "—"}</td>
      </tr>`,
    )
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Medication List — ${escapeHtml(patientName)}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #0f172a; margin: 0; padding: 24px; }
    .header { margin-bottom: 20px; border-bottom: 2px solid ${brand.border}; padding-bottom: 12px; }
    h1 { font-size: 20px; margin: 0 0 4px; color: ${brand.title}; }
    .meta { font-size: 13px; color: #64748b; }
    .actions { margin: 16px 0 20px; }
    button { background: ${brand.strong}; color: #fff; border: none; padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; }
    button:hover { background: ${brand.text}; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th { text-align: left; background: ${brand.soft}; color: ${brand.text}; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; padding: 8px 10px; border-bottom: 2px solid ${brand.border}; }
    td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; word-break: break-word; }
    td.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; color: #475569; }
    td.name { font-weight: 600; color: #0f172a; }
    td.date { white-space: nowrap; color: #475569; }
    tr:nth-child(even) td { background: #fafafa; }
    .empty { text-align: center; padding: 32px; color: #94a3b8; }
    @media print {
      .actions { display: none; }
      body { padding: 12px; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>Medication List</h1>
    <div class="meta"><strong>Patient:</strong> ${escapeHtml(patientName)} &nbsp;|&nbsp; <strong>MRN:</strong> ${escapeHtml(mrn)}</div>
    <div class="meta">Generated: ${generatedAt}</div>
  </div>
  <div class="actions">
    <button type="button" onclick="window.print()">Download / Print</button>
  </div>
  ${
    meds.length
      ? `<table>
    <thead>
      <tr>
        <th>Medication ID</th>
        <th>Medication Name</th>
        <th>Dosage</th>
        <th>Frequency</th>
        <th>Route</th>
        <th>Prescribing Physician</th>
        <th>Start Date</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>`
      : `<p class="empty">No medications on record.</p>`
  }
</body>
</html>`;
};

/** Autocomplete search against RxNorm catalog; search only after click/type. */
const MedicationNameSearchField = ({
  value,
  medicationId,
  onChangeName,
  onSelectCatalog,
  onClearCatalog,
  inputClassName = fieldClass,
  placeholder = "Search medication…",
  showDefaultSuggestions = true,
}) => {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [searchEnabled, setSearchEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [isDefaultList, setIsDefaultList] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const defaultCacheRef = useRef(null);
  const skipDebounceOnceRef = useRef(false);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setSearchEnabled(false);
  }, []);

  const fetchSuggestions = useCallback(async (q, { openMenu = true } = {}) => {
    const trimmed = String(q || "").trim();
    if (!trimmed && defaultCacheRef.current) {
      requestIdRef.current += 1;
      setResults(defaultCacheRef.current);
      setIsDefaultList(true);
      setLoading(false);
      if (openMenu) setOpen(true);
      return;
    }

    const reqId = ++requestIdRef.current;
    setLoading(true);
    try {
      const res = await searchRxnormMedications({
        q: trimmed,
        limit: trimmed ? 12 : 10,
      });
      if (reqId !== requestIdRef.current) return;
      const payload = res.result || res;
      const meds = payload.medications || [];
      if (!trimmed) defaultCacheRef.current = meds;
      setResults(meds);
      setIsDefaultList(!trimmed);
      if (openMenu) setOpen(true);
    } catch {
      if (reqId !== requestIdRef.current) return;
      setResults([]);
      setIsDefaultList(false);
      if (openMenu) setOpen(true);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      closeMenu();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [closeMenu]);

  useEffect(() => {
    if (!searchEnabled) return undefined;
    if (skipDebounceOnceRef.current) {
      skipDebounceOnceRef.current = false;
      return undefined;
    }
    const q = resolveMedicationSearchQuery(value, medicationId);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (q.length === 0) {
      if (showDefaultSuggestions) {
        debounceRef.current = setTimeout(
          () => fetchSuggestions(""),
          SEARCH_DEBOUNCE_MS,
        );
        return () => clearTimeout(debounceRef.current);
      }
      setLoading(false);
      return undefined;
    }

    if (q.length < 2 && !medicationId) {
      setResults([]);
      setIsDefaultList(false);
      setLoading(false);
      setOpen(true);
      return undefined;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(q);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, medicationId, searchEnabled, fetchSuggestions, showDefaultSuggestions]);

  const enableSearch = () => {
    skipDebounceOnceRef.current = true;
    setSearchEnabled(true);
    const q = resolveMedicationSearchQuery(value, medicationId);
    if (q.length >= 1) fetchSuggestions(q);
    else if (showDefaultSuggestions) fetchSuggestions("");
  };

  const activeQuery = resolveMedicationSearchQuery(value, medicationId);
  const showEmptyAdd =
    open &&
    searchEnabled &&
    !loading &&
    activeQuery.length >= 2 &&
    !medicationId &&
    results.length === 0;

  return (
    <div ref={wrapRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        className={`${inputClassName} pr-9`}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onClick={enableSearch}
        onChange={(e) => {
          setSearchEnabled(true);
          const next = e.target.value;
          onChangeName(next);
          if (medicationId) onClearCatalog?.();
        }}
      />
      <SearchFieldStatusIcon loading={loading} />
      <SearchMenuPortal
        anchorRef={inputRef}
        menuRef={menuRef}
        open={open && (loading || results.length > 0 || showEmptyAdd)}
      >
        {loading && results.length === 0 ? (
          <SearchMenuLoading />
        ) : results.length > 0 ? (
          <>
            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-50 border-b border-slate-100 sticky top-0">
              {isDefaultList ? "Suggested medications" : "Search results"}
              {loading ? " · Updating…" : ""}
            </div>
            <ul>
              {results.map((item) => (
                <li key={`${item.medicationId}-${item.tty}-${item.medicationName}`}>
                  <button
                    type="button"
                    className="w-full text-left px-3.5 py-2.5 hover:bg-indigo-50 border-b border-slate-50 last:border-0 transition-colors"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onSelectCatalog(item);
                      closeMenu();
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-sm font-medium text-slate-900 leading-snug">
                        {item.medicationName}
                      </span>
                      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">
                        {item.tty}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                      <span className="font-mono text-indigo-700">ID: {item.medicationId}</span>
                      {item.dosage ? <span>Dose: {item.dosage}</span> : null}
                      {item.route ? <span>Route: {item.route}</span> : null}
                      {item.genericName ? <span>Generic: {item.genericName}</span> : null}
                      {item.brandName ? <span>Brand: {item.brandName}</span> : null}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : showEmptyAdd ? (
          <>
            <div className="px-3.5 py-3 text-[12px] text-slate-600 border-b border-slate-100">
              No catalog match found for this search.
            </div>
            <div className="px-3 py-2.5 bg-slate-50">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  closeMenu();
                  setAddOpen(true);
                }}
              >
                <PlusIcon size={13} />
                Add Medication
              </button>
            </div>
          </>
        ) : null}
      </SearchMenuPortal>
      <AddMedicationModal
        isOpen={addOpen}
        initialName={value}
        onClose={() => setAddOpen(false)}
        onCreated={(medication) => {
          defaultCacheRef.current = null;
          onSelectCatalog(medication);
          setAddOpen(false);
          closeMenu();
        }}
      />
    </div>
  );
};

const RowActions = ({
  med,
  disabled,
  editing,
  saving,
  onEdit,
  onSave,
  onCancel,
  onDelete,
}) => (
  <div className="inline-flex items-center justify-end gap-0.5">
    {editing ? (
      <>
        <button
          type="button"
          disabled={disabled || saving}
          onClick={onSave}
          className="inline-flex items-center justify-center h-7 w-7 rounded-md text-indigo-600 hover:bg-indigo-50 border border-indigo-200 transition-colors disabled:opacity-50"
          title="Save"
          aria-label={`Save ${med.medicationName}`}
        >
          {saving ? (
            <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <CheckIcon size={14} />
          )}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={onCancel}
          className="inline-flex items-center justify-center h-7 w-7 rounded-md text-slate-500 hover:bg-slate-100 border border-slate-200 transition-colors disabled:opacity-50"
          title="Cancel"
          aria-label="Cancel edit"
        >
          <CloseIcon size={14} />
        </button>
      </>
    ) : (
      <>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onEdit(med)}
          className="inline-flex items-center justify-center h-7 w-7 rounded-md text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 border border-transparent hover:border-indigo-100 transition-colors disabled:opacity-50"
          title="Edit medication"
          aria-label={`Edit ${med.medicationName}`}
        >
          <EditIcon size={14} />
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onDelete(med)}
          className="inline-flex items-center justify-center h-7 w-7 rounded-md text-slate-500 hover:text-red-700 hover:bg-red-50 border border-transparent hover:border-red-100 transition-colors disabled:opacity-50"
          title="Delete medication"
          aria-label={`Delete ${med.medicationName}`}
        >
          <DeleteIcon size={14} />
        </button>
      </>
    )}
  </div>
);

/** Two-line add form — ID + name search on top; details + Add on second row. */
const InlineAddMedicationForm = ({
  form,
  setForm,
  saving,
  onAdd,
  physicianOptions = [],
}) => {
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="px-4 py-4 border-b border-indigo-100 bg-gradient-to-b from-indigo-50/60 to-white">
      <div className="mb-2.5 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-800">
            Add medication
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Click name to see suggestions, or type to search / add manually.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {/* Line 1 — Medication ID + Name search (primary) */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-3 min-w-0">
            <label className={labelClass}>Medication ID</label>
            <input
              type="text"
              className={`${fieldClassLg} bg-slate-50 text-slate-800`}
              value={form.medicationId}
              readOnly
              placeholder="Auto from catalog"
              title="RxNorm RxCUI — filled when you pick a suggestion"
            />
          </div>
          <div className="md:col-span-9 min-w-0">
            <label className={labelClass}>
              Medication Name <span className="text-red-500">*</span>
            </label>
            <MedicationNameSearchField
              value={form.medicationName}
              medicationId={form.medicationId}
              inputClassName={fieldClassLg}
              placeholder="Click for suggestions, or type to search…"
              showDefaultSuggestions
              onChangeName={(name) => set("medicationName", name)}
              onClearCatalog={() =>
                setForm((prev) => ({
                  ...prev,
                  medicationId: "",
                  catalogMeta: null,
                }))
              }
              onSelectCatalog={(item) =>
                setForm((prev) => applyCatalogSelection(prev, item))
              }
            />
          </div>
        </div>

        {/* Line 2 — dosage / schedule / physician / date + Add */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end pt-1 border-t border-indigo-100/80">
          <div className="lg:col-span-2 min-w-0">
            <label className={labelClass}>
              Dosage <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={fieldClass}
              value={form.dosage}
              onChange={(e) => set("dosage", e.target.value)}
              placeholder="10 MG"
            />
          </div>
          <div className="lg:col-span-2 min-w-0">
            <label className={labelClass}>
              Frequency <span className="text-red-500">*</span>
            </label>
            <select
              className={fieldClass}
              value={form.frequencySelect}
              onChange={(e) => set("frequencySelect", e.target.value)}
            >
              {MEDICATION_FREQUENCIES.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
          <div className="lg:col-span-2 min-w-0">
            <label className={labelClass}>
              Route <span className="text-red-500">*</span>
            </label>
            <select
              className={fieldClass}
              value={form.route}
              onChange={(e) => set("route", e.target.value)}
            >
              {MEDICATION_ROUTES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="lg:col-span-3 min-w-0">
            <label className={labelClass}>Physician</label>
            <PhysicianField
              options={physicianOptions}
              value={form.prescribingPhysician}
              onChange={(v) => set("prescribingPhysician", v)}
              inputClassName={fieldClass}
              placeholder="Type physician name"
            />
          </div>
          <div className="lg:col-span-2 min-w-[9.75rem]">
            <UsDateField
              label="Start Date"
              value={form.startDate}
              onChange={(v) => set("startDate", v)}
            />
          </div>
          <div className="lg:col-span-1 flex items-end">
            <button
              type="button"
              onClick={onAdd}
              disabled={saving}
              className={`${addBtnClass} w-full`}
            >
              {saving ? (
                <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <PlusIcon size={14} />
              )}
              Add
            </button>
          </div>
        </div>

        {form.frequencySelect === "Other" ? (
          <div className="min-w-0">
            <label className={labelClass}>Custom Frequency</label>
            <input
              type="text"
              className={fieldClass}
              value={form.frequencyOther}
              onChange={(e) => set("frequencyOther", e.target.value)}
              placeholder="Enter custom frequency"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

const PatientMedicationSection = ({ patientId, patient, chartId, canEdit }) => {
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    limit: PAGE_SIZE,
  });

  const [editingMedId, setEditingMedId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [addForm, setAddForm] = useState(() => ({
    ...emptyForm(),
    prescribingPhysician: patient?.primaryPhysician || "",
  }));
  const [addSaving, setAddSaving] = useState(false);

  const [printing, setPrinting] = useState(false);
  const { confirm, confirmDialog } = useConfirm();

  const [sectionOpen, onSectionOpenChange] = useCollapseOpenForCount(
    medications.length,
    `${patientId}:${chartId || ""}`,
  );

  const physicianOptions = buildPhysicians(patient).filter(
    (p) => String(p.physicianName || "").trim(),
  );

  const setEditField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const loadMedications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPatientMedications(patientId, {
        page,
        limit: PAGE_SIZE,
        ...(chartId ? { chartId } : {}),
      });
      const result = res.result || res;
      setMedications(result.medications || []);
      setPagination({
        total: result.total ?? 0,
        totalPages: result.totalPages ?? 1,
        limit: result.limit ?? PAGE_SIZE,
      });
    } catch (err) {
      toast.error(err.message || "Failed to load medications");
    } finally {
      setLoading(false);
    }
  }, [patientId, page, chartId]);

  useEffect(() => {
    loadMedications();
  }, [loadMedications]);

  const handleInlineAdd = async () => {
    if (editingMedId) {
      toast.error("Finish or cancel the current edit first");
      return;
    }
    const error = validateForm(addForm);
    if (error) {
      toast.error(error);
      return;
    }
    setAddSaving(true);
    try {
      await createPatientMedication(patientId, {
        ...formToPayload(addForm),
        ...(chartId ? { chartId: Number(chartId) } : {}),
      });
      toast.success("Medication added");
      setAddForm({
        ...emptyForm(),
        prescribingPhysician: patient?.primaryPhysician || "",
      });
      await loadMedications();
    } catch (err) {
      toast.error(err.message || "Failed to add medication");
    } finally {
      setAddSaving(false);
    }
  };

  const openEdit = (med) => {
    setEditingMedId(med.id);
    setForm(medicationToForm(med));
  };

  const cancelEdit = () => {
    if (saving) return;
    setEditingMedId(null);
    setForm(emptyForm());
  };

  const handleSaveEdit = async () => {
    if (!editingMedId) return;
    const error = validateForm(form);
    if (error) {
      toast.error(error);
      return;
    }

    setSaving(true);
    try {
      await updatePatientMedication(
        patientId,
        editingMedId,
        formToPayload(form),
      );
      toast.success("Medication updated");
      setEditingMedId(null);
      setForm(emptyForm());
      await loadMedications();
    } catch (err) {
      toast.error(err.message || "Failed to save medication");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (med) => {
    const ok = await confirm({
      title: "Delete medication?",
      message: `"${med.medicationName}" will be permanently removed from the medication list. This cannot be undone.`,
    });
    if (!ok) return;
    // Only discard the in-progress edit once the delete is actually confirmed.
    if (editingMedId === med.id) cancelEdit();
    try {
      await deletePatientMedication(patientId, med.id);
      toast.success("Medication deleted");
      await loadMedications();
    } catch (err) {
      toast.error(err.message || "Failed to delete medication");
    }
  };

  const handlePrint = async () => {
    setPrinting(true);
    try {
      const res = await getPatientMedicationPrintData(patientId, {
        ...(chartId ? { chartId: Number(chartId) } : {}),
      });
      const printData = res.result || res;
      const html = buildPrintHtml(printData);
      const win = window.open("", "_blank");
      if (!win) {
        toast.error("Pop-up blocked. Allow pop-ups to print the medication list.");
        return;
      }
      win.document.write(html);
      win.document.close();
    } catch (err) {
      toast.error(err.message || "Failed to generate print list");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <>
      <CollapsibleSection
        title="Medication Management"
        icon={<PillIcon size={14} />}
        tone={MEDICATION_TONE}
        open={sectionOpen}
        onOpenChange={onSectionOpenChange}
        actions={
          <button
            type="button"
            onClick={handlePrint}
            disabled={printing}
            className={outlineNeutralBtn}
          >
            {printing ? (
              <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <PrintIcon size={14} />
            )}
            Print List
          </button>
        }
      >
        {canEdit && (
          <InlineAddMedicationForm
            form={addForm}
            setForm={setAddForm}
            saving={addSaving}
            onAdd={handleInlineAdd}
            physicianOptions={physicianOptions}
          />
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <p className="px-3 py-6 text-center text-sm text-slate-400 animate-pulse">
              Loading medications...
            </p>
          ) : medications.length === 0 ? (
            <div className="px-3 py-10 text-center">
              <PillIcon size={28} className="mx-auto text-indigo-200 mb-2" />
              <p className="text-sm font-medium text-slate-500">No medications yet</p>
              <p className="text-[11px] text-slate-400 mt-1">
                {canEdit
                  ? "Search the medication catalog to auto-fill, or type a name manually."
                  : "No medications have been recorded for this patient."}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm min-w-[960px]">
              <colgroup>
                <col className="w-[11%]" />
                <col className="w-[20%]" />
                <col className="w-[9%]" />
                <col className="w-[10%]" />
                <col className="w-[9%]" />
                <col className="w-[22%]" />
                <col className="w-[11%]" />
                {canEdit ? <col className="w-[8%]" /> : null}
              </colgroup>
              <thead
                className={`sticky top-0 z-10 ${PATIENT_THEME.tableHead.row}`}
              >
                <tr
                  className={`border-b text-left text-[11px] uppercase tracking-wide ${PATIENT_THEME.tableHead.className}`}
                >
                  <th className="px-3 py-2 font-semibold">Medication ID</th>
                  <th className="px-3 py-2 font-semibold">Medication Name</th>
                  <th className="px-3 py-2 font-semibold">Dosage</th>
                  <th className="px-3 py-2 font-semibold hidden md:table-cell">Frequency</th>
                  <th className="px-3 py-2 font-semibold hidden lg:table-cell">Route</th>
                  <th className="px-3 py-2 font-semibold hidden lg:table-cell">Prescribing Physician</th>
                  <th className="px-3 py-2 font-semibold hidden sm:table-cell">Start Date</th>
                  {canEdit && (
                    <th className="px-3 py-2 font-semibold text-right">
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {medications.map((med) => {
                  const isEditing = editingMedId === med.id;
                  return (
                    <tr
                      key={med.id}
                      className={`border-b border-slate-100 last:border-0 ${
                        isEditing
                          ? "bg-indigo-50/50"
                          : "hover:bg-slate-50/70"
                      }`}
                    >
                      {isEditing ? (
                        <>
                          <td className="px-2 py-1.5 align-top">
                            <input
                              type="text"
                              className={`${cellFieldClass} bg-slate-50`}
                              value={form.medicationId}
                              readOnly
                              placeholder="—"
                              title="From RxNorm catalog"
                            />
                          </td>
                          <td className="px-2 py-1.5 align-top">
                            <MedicationNameSearchField
                              value={form.medicationName}
                              medicationId={form.medicationId}
                              inputClassName={cellFieldClass}
                              placeholder="Search or type"
                              onChangeName={(name) =>
                                setEditField("medicationName", name)
                              }
                              onClearCatalog={() =>
                                setForm((prev) => ({
                                  ...prev,
                                  medicationId: "",
                                  catalogMeta: null,
                                }))
                              }
                              onSelectCatalog={(item) =>
                                setForm((prev) => applyCatalogSelection(prev, item))
                              }
                            />
                          </td>
                          <td className="px-2 py-1.5 align-top">
                            <input
                              type="text"
                              className={cellFieldClass}
                              value={form.dosage}
                              onChange={(e) =>
                                setEditField("dosage", e.target.value)
                              }
                              placeholder="Dosage"
                            />
                          </td>
                          <td className="px-2 py-1.5 align-top">
                            <select
                              className={cellFieldClass}
                              value={form.frequencySelect}
                              onChange={(e) =>
                                setEditField("frequencySelect", e.target.value)
                              }
                            >
                              {MEDICATION_FREQUENCIES.map((f) => (
                                <option key={f} value={f}>
                                  {f}
                                </option>
                              ))}
                            </select>
                            {form.frequencySelect === "Other" ? (
                              <input
                                type="text"
                                className={`${cellFieldClass} mt-1`}
                                value={form.frequencyOther}
                                onChange={(e) =>
                                  setEditField("frequencyOther", e.target.value)
                                }
                                placeholder="Custom frequency"
                              />
                            ) : null}
                          </td>
                          <td className="px-2 py-1.5 align-top">
                            <select
                              className={cellFieldClass}
                              value={form.route}
                              onChange={(e) =>
                                setEditField("route", e.target.value)
                              }
                            >
                              {MEDICATION_ROUTES.map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1.5 align-top min-w-0">
                            <PhysicianField
                              options={physicianOptions}
                              value={form.prescribingPhysician}
                              onChange={(v) =>
                                setEditField("prescribingPhysician", v)
                              }
                              inputClassName={cellFieldClass}
                              placeholder="Physician"
                            />
                          </td>
                          <td className="px-2 py-1.5 align-top">
                            <UsDateField
                              value={form.startDate}
                              onChange={(v) => setEditField("startDate", v)}
                              inputClassName={cellFieldClass}
                            />
                          </td>
                          <td className="px-2 py-1.5 align-top text-right">
                            <RowActions
                              med={med}
                              editing
                              saving={saving}
                              onSave={handleSaveEdit}
                              onCancel={cancelEdit}
                            />
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2 text-slate-600 font-mono text-xs">
                            {med.medicationId || "—"}
                          </td>
                          <td className="px-3 py-2 font-medium text-slate-900 break-words">
                            {med.medicationName}
                          </td>
                          <td className="px-3 py-2 text-slate-700 break-words">
                            {med.dosage}
                          </td>
                          <td className="px-3 py-2 text-slate-600 hidden md:table-cell break-words">
                            {med.frequency}
                          </td>
                          <td className="px-3 py-2 text-slate-600 hidden lg:table-cell break-words">
                            {med.route}
                          </td>
                          <td
                            className="px-3 py-2 text-slate-600 hidden lg:table-cell break-words"
                            title={med.prescribingPhysician || undefined}
                          >
                            {med.prescribingPhysician || "—"}
                          </td>
                          <td className="px-3 py-2 text-slate-600 hidden sm:table-cell whitespace-nowrap">
                            {displayDateUS(med.startDate) || "—"}
                          </td>
                          {canEdit && (
                            <td className="px-3 py-2 text-right">
                              <RowActions
                                med={med}
                                disabled={Boolean(editingMedId) || saving}
                                onEdit={openEdit}
                                onDelete={handleDelete}
                              />
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {!loading && pagination.totalPages > 1 && (
          <div className="px-3 py-2 border-t border-slate-100">
            <Pagination
              currentPage={page}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              itemsPerPage={pagination.limit}
              onPageChange={setPage}
            />
          </div>
        )}
      </CollapsibleSection>
      {confirmDialog}
    </>
  );
};

export default PatientMedicationSection;
